import React from "react";
import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SidebarAdmin from "../../menu/SidebarAdmin";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import { useAuth } from "../../auth/AuthContext";
import { isSuperAdmin } from "../../../utils/accessControl";
import "./AdminRealgemsOrders.css";

const PAGE_SIZE = 50;

const ORDER_STATUSES = {
  PENDING: "PENDING",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  CANCELED: "CANCELED",
};

const ORDER_STATUS_META = {
  [ORDER_STATUSES.PENDING]: { label: "Aguardando analise", className: "pending" },
  [ORDER_STATUSES.IN_PROGRESS]: { label: "Em andamento", className: "in-progress" },
  [ORDER_STATUSES.COMPLETED]: { label: "Concluido", className: "completed" },
  [ORDER_STATUSES.CANCELED]: { label: "Cancelado", className: "canceled" },
};

const tableCellSx = {
  borderBottom: "1px solid #eef2f7",
  color: "#334155",
  fontSize: "0.88rem",
  py: 1.55,
  whiteSpace: "nowrap",
};

const headCellSx = {
  borderBottom: "1px solid #e2e8f0",
  color: "#64748b",
  fontSize: "0.78rem",
  fontWeight: 800,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  backgroundColor: "#f8fafc",
};

const formatCnpj = (value = "") => {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 14);

  if (!digits) return "-";
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5)}`;
  if (digits.length <= 12) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8)}`;
  }

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`;
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
};

const shortenText = (value, maxLen = 120) => {
  const text = String(value || "").trim();
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen - 3)}...`;
};

const getStatusMeta = (status) => ORDER_STATUS_META[status] || ORDER_STATUS_META.PENDING;

const AdminRealgemsOrders = () => {
  const { user } = useAuth();
  const canDeleteOrders = isSuperAdmin(user?.access_type);
  const [menuClose, setMenuClose] = React.useState(false);
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [reloadKey, setReloadKey] = React.useState(0);
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });
  const [viewingOrder, setViewingOrder] = React.useState(null);
  const [statusOrder, setStatusOrder] = React.useState(null);
  const [statusValue, setStatusValue] = React.useState(ORDER_STATUSES.PENDING);
  const [adminNotes, setAdminNotes] = React.useState("");
  const [statusSubmitting, setStatusSubmitting] = React.useState(false);
  const [statusError, setStatusError] = React.useState("");
  const [deletingOrder, setDeletingOrder] = React.useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState("");

  const toggleMenu = () => setMenuClose((prev) => !prev);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 250);

    return () => window.clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setViewingOrder(null);
        setStatusOrder(null);
        if (!deleteSubmitting) setDeletingOrder(null);
        setStatusError("");
        setDeleteError("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [deleteSubmitting]);

  React.useEffect(() => {
    let active = true;

    const loadOrders = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await Api.get("/admin/realgems/orders", {
          params: {
            page: currentPage,
            pageSize: PAGE_SIZE,
            search: debouncedSearch || undefined,
          },
        });

        if (!active) return;

        setOrders(Array.isArray(response?.data?.items) ? response.data.items : []);
        setCurrentPage(Number(response?.data?.pagination?.page || currentPage || 1));
        setPagination({
          pageSize: Number(response?.data?.pagination?.pageSize || PAGE_SIZE),
          total: Number(response?.data?.pagination?.total || 0),
          totalPages: Number(response?.data?.pagination?.totalPages || 1),
        });
      } catch (err) {
        if (!active) return;

        console.error("Erro ao carregar pedidos Realgems no admin:", err);
        setError(err?.response?.data?.message || "Nao foi possivel carregar os pedidos Realgems.");
        setOrders([]);
        setPagination((current) => ({
          pageSize: current.pageSize,
          total: 0,
          totalPages: 1,
        }));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadOrders();

    return () => {
      active = false;
    };
  }, [currentPage, debouncedSearch, reloadKey]);

  const replaceOrderInState = React.useCallback((updatedOrder) => {
    if (!updatedOrder?.id) return;

    setOrders((current) =>
      current.map((item) => (Number(item.id) === Number(updatedOrder.id) ? updatedOrder : item))
    );
    setViewingOrder((current) =>
      Number(current?.id) === Number(updatedOrder.id) ? updatedOrder : current
    );
  }, []);

  const closeViewModal = () => setViewingOrder(null);

  const closeStatusModal = () => {
    setStatusOrder(null);
    setStatusValue(ORDER_STATUSES.PENDING);
    setAdminNotes("");
    setStatusError("");
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setDeletingOrder(null);
    setDeleteError("");
  };

  const openStatusModal = (order) => {
    setViewingOrder(null);
    setStatusOrder(order);
    setStatusValue(order.status || ORDER_STATUSES.PENDING);
    setAdminNotes(order.adminNotes || "");
    setStatusError("");
  };

  const openDeleteModal = (order) => {
    setViewingOrder(null);
    setStatusOrder(null);
    setDeletingOrder(order);
    setDeleteError("");
    setSuccess("");
  };

  const handleStatusSubmit = async (event) => {
    event.preventDefault();
    if (!statusOrder?.id) return;

    try {
      setStatusSubmitting(true);
      setStatusError("");

      const response = await Api.patch(`/admin/realgems/orders/${statusOrder.id}/status`, {
        status: statusValue,
        adminNotes,
      });

      if (response?.data?.item) {
        replaceOrderInState(response.data.item);
      }

      closeStatusModal();
    } catch (err) {
      console.error("Erro ao atualizar status do pedido Realgems:", err);
      setStatusError(err?.response?.data?.message || "Nao foi possivel atualizar o status do pedido.");
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!deletingOrder?.id || !canDeleteOrders) return;

    try {
      setDeleteSubmitting(true);
      setDeleteError("");
      setError("");
      setSuccess("");

      const response = await Api.delete(`/admin/realgems/orders/${deletingOrder.id}`);
      const deletedId = Number(response?.data?.deletedId || deletingOrder.id);

      setOrders((current) => current.filter((item) => Number(item.id) !== deletedId));
      setPagination((current) => ({
        ...current,
        total: Math.max(0, current.total - 1),
        totalPages: Math.max(1, Math.ceil(Math.max(0, current.total - 1) / current.pageSize)),
      }));
      setSuccess(response?.data?.message || "Pedido excluído com sucesso.");
      setDeletingOrder(null);

      if (orders.length === 1 && currentPage > 1) {
        setCurrentPage((page) => Math.max(1, page - 1));
      } else {
        setReloadKey((value) => value + 1);
      }
    } catch (err) {
      console.error("Erro ao excluir pedido Realgems:", err);
      setDeleteError(err?.response?.data?.message || "Não foi possível excluir o pedido.");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const hasOrders = orders.length > 0;

  return (
    <div className="app-container admin-realgems-orders-page">
      <SidebarAdmin close={menuClose} realgemsOrdersActive="active" />

      <main className="main-content admin-realgems-orders-main-content">
        <Topbar page="Pedidos Realgems" onToggleMenu={toggleMenu} />

        <section className="admin-realgems-orders-panel">
          <div className="admin-realgems-orders-panel-header">
            <div>
              <span className="admin-realgems-orders-kicker">Realgems</span>
              <h1>Pedidos cadastrados</h1>
              <p>
                Acompanhe todos os pedidos enviados no portal Realgems, com o usuario responsavel e o
                cliente selecionado em cada solicitacao.
              </p>
            </div>
          </div>

          <div className="admin-realgems-orders-toolbar">
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
              placeholder="Buscar por cliente, CNPJ, usuario, texto do pedido ou status"
              className="admin-realgems-orders-search"
            />
          </div>

          <div className="admin-realgems-orders-summary">
            {loading
              ? "Carregando pedidos..."
              : `${pagination.total} pedido(s) encontrado(s). Pagina ${currentPage} de ${pagination.totalPages}.`}
          </div>

          {error ? <div className="admin-realgems-orders-message error">{error}</div> : null}
          {success ? <div className="admin-realgems-orders-message success">{success}</div> : null}

          {loading ? (
            <div className="admin-realgems-orders-empty">Carregando pedidos...</div>
          ) : hasOrders ? (
            <>
              <TableContainer component={Paper} className="admin-realgems-orders-table-wrap">
                <Table sx={{ minWidth: canDeleteOrders ? 1280 : 1160 }} aria-label="Tabela administrativa de pedidos Realgems">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCellSx}>Cliente</TableCell>
                      <TableCell sx={headCellSx}>CNPJ</TableCell>
                      <TableCell sx={headCellSx}>Criado por</TableCell>
                      <TableCell sx={headCellSx}>Status</TableCell>
                      <TableCell sx={headCellSx}>Pedido</TableCell>
                      <TableCell sx={headCellSx}>Atualizado em</TableCell>
                      <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Acoes</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {orders.map((order) => {
                      const statusMeta = getStatusMeta(order.status);

                      return (
                        <TableRow key={order.id} hover>
                          <TableCell sx={tableCellSx}>
                            <strong className="admin-realgems-orders-table-strong">
                              {order.clientTradeName || order.clientCompanyLegalName || "-"}
                            </strong>
                          </TableCell>
                          <TableCell sx={tableCellSx}>{formatCnpj(order.clientCnpj)}</TableCell>
                          <TableCell sx={tableCellSx}>{order.ownerUserName || "Nao informado"}</TableCell>
                          <TableCell sx={tableCellSx}>
                            <Chip
                              label={statusMeta.label}
                              size="small"
                              className={`admin-realgems-orders-status-badge ${statusMeta.className}`}
                            />
                          </TableCell>
                          <TableCell sx={{ ...tableCellSx, whiteSpace: "normal", minWidth: 280 }}>
                            {shortenText(order.orderNotes, 140)}
                          </TableCell>
                          <TableCell sx={tableCellSx}>{formatDateTime(order.updatedAt)}</TableCell>
                          <TableCell sx={tableCellSx} align="right">
                            <div className="admin-realgems-orders-table-actions">
                              <button
                                type="button"
                                className="admin-realgems-orders-btn secondary"
                                onClick={() => setViewingOrder(order)}
                              >
                                Ver pedido
                              </button>
                              <button
                                type="button"
                                className="admin-realgems-orders-btn primary"
                                onClick={() => openStatusModal(order)}
                              >
                                Status
                              </button>
                              {canDeleteOrders ? (
                                <button
                                  type="button"
                                  className="admin-realgems-orders-btn danger"
                                  onClick={() => openDeleteModal(order)}
                                >
                                  Excluir
                                </button>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {pagination.totalPages > 1 ? (
                <div className="admin-realgems-orders-pagination">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage <= 1}
                  >
                    Anterior
                  </button>

                  <span>
                    Pagina {currentPage} de {pagination.totalPages}
                  </span>

                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(pagination.totalPages, page + 1))}
                    disabled={currentPage >= pagination.totalPages}
                  >
                    Proxima
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <div className="admin-realgems-orders-empty">
              {debouncedSearch
                ? "Nenhum pedido encontrado com esse filtro."
                : "Nenhum pedido Realgems cadastrado ainda."}
            </div>
          )}
        </section>

        <div
          className={`admin-realgems-orders-modal-backdrop ${deletingOrder ? "is-open" : ""}`}
          onClick={closeDeleteModal}
          aria-hidden={!deletingOrder}
        />

        <section
          className={`admin-realgems-orders-modal ${deletingOrder ? "is-open" : ""}`}
          aria-hidden={!deletingOrder}
          aria-modal="true"
          role="dialog"
          aria-labelledby="delete-realgems-order-title"
        >
          <div className="admin-realgems-orders-modal-card admin-realgems-orders-delete-card">
            <div className="admin-realgems-orders-modal-header">
              <div>
                <span className="admin-realgems-orders-kicker danger">Exclusão permanente</span>
                <h2 id="delete-realgems-order-title">Excluir pedido</h2>
              </div>
            </div>

            {deletingOrder ? (
              <div className="admin-realgems-orders-delete-content">
                <p>
                  Tem certeza de que deseja excluir o pedido de{" "}
                  <strong>
                    {deletingOrder.clientTradeName || deletingOrder.clientCompanyLegalName || "cliente não identificado"}
                  </strong>
                  ?
                </p>
                <div className="admin-realgems-orders-delete-summary">
                  <span>Pedido #{deletingOrder.id}</span>
                  <strong>{shortenText(deletingOrder.orderNotes, 240)}</strong>
                </div>
                <div className="admin-realgems-orders-delete-warning">
                  Esta ação não pode ser desfeita. O cliente e os demais registros permanecerão
                  cadastrados.
                </div>

                {deleteError ? (
                  <div className="admin-realgems-orders-message error">{deleteError}</div>
                ) : null}

                <div className="admin-realgems-orders-delete-actions">
                  <button
                    type="button"
                    className="admin-realgems-orders-btn secondary"
                    onClick={closeDeleteModal}
                    disabled={deleteSubmitting}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="admin-realgems-orders-btn danger"
                    onClick={handleDeleteOrder}
                    disabled={deleteSubmitting}
                  >
                    {deleteSubmitting ? "Excluindo..." : "Excluir definitivamente"}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <div
          className={`admin-realgems-orders-modal-backdrop ${viewingOrder ? "is-open" : ""}`}
          onClick={closeViewModal}
          aria-hidden={!viewingOrder}
        />

        <section
          className={`admin-realgems-orders-modal ${viewingOrder ? "is-open" : ""}`}
          aria-hidden={!viewingOrder}
        >
          <div className="admin-realgems-orders-modal-card">
            <div className="admin-realgems-orders-modal-header">
              <div>
                <span className="admin-realgems-orders-kicker">Pedido</span>
                <h2>Visualizar pedido</h2>
              </div>

              <button
                type="button"
                className="admin-realgems-orders-btn secondary"
                onClick={closeViewModal}
              >
                Fechar
              </button>
            </div>

            {viewingOrder ? (
              <div className="admin-realgems-orders-view-grid">
                <div><strong>Status:</strong> {getStatusMeta(viewingOrder.status).label}</div>
                <div><strong>Criado por:</strong> {viewingOrder.ownerUserName || "Nao informado"}</div>
                <div><strong>Cliente:</strong> {viewingOrder.clientTradeName || "-"}</div>
                <div><strong>Razao social:</strong> {viewingOrder.clientCompanyLegalName || "-"}</div>
                <div><strong>CNPJ:</strong> {formatCnpj(viewingOrder.clientCnpj)}</div>
                <div><strong>Criado em:</strong> {formatDateTime(viewingOrder.createdAt)}</div>
                <div><strong>Atualizado em:</strong> {formatDateTime(viewingOrder.updatedAt)}</div>
                <div><strong>E-mail do usuario:</strong> {viewingOrder.ownerUserEmail || "-"}</div>
                <div className="admin-realgems-orders-view-grid-wide">
                  <strong>Descricao completa do pedido:</strong> {viewingOrder.orderNotes || "-"}
                </div>
                <div className="admin-realgems-orders-view-grid-wide">
                  <strong>Observacao interna:</strong> {viewingOrder.adminNotes || "-"}
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <div
          className={`admin-realgems-orders-modal-backdrop ${statusOrder ? "is-open" : ""}`}
          onClick={closeStatusModal}
          aria-hidden={!statusOrder}
        />

        <section
          className={`admin-realgems-orders-modal ${statusOrder ? "is-open" : ""}`}
          aria-hidden={!statusOrder}
        >
          <div className="admin-realgems-orders-modal-card">
            <div className="admin-realgems-orders-modal-header">
              <div>
                <span className="admin-realgems-orders-kicker">Status</span>
                <h2>Atualizar pedido</h2>
                {statusOrder ? (
                  <p>
                    Ajuste o andamento do pedido de <strong>{statusOrder.clientTradeName}</strong>.
                  </p>
                ) : null}
              </div>

              <button
                type="button"
                className="admin-realgems-orders-btn secondary"
                onClick={closeStatusModal}
              >
                Fechar
              </button>
            </div>

            <form onSubmit={handleStatusSubmit} className="admin-realgems-orders-status-form">
              <div className="admin-realgems-orders-status-options">
                {Object.values(ORDER_STATUSES).map((status) => {
                  const meta = getStatusMeta(status);

                  return (
                    <label
                      key={status}
                      className={`admin-realgems-orders-status-option ${
                        statusValue === status ? "selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="orderStatus"
                        value={status}
                        checked={statusValue === status}
                        onChange={() => setStatusValue(status)}
                      />
                      <span className={`admin-realgems-orders-status-badge ${meta.className}`}>
                        {meta.label}
                      </span>
                    </label>
                  );
                })}
              </div>

              <label className="admin-realgems-orders-status-notes">
                <span>Observacao interna</span>
                <textarea
                  value={adminNotes}
                  onChange={(event) => {
                    setAdminNotes(event.target.value);
                    setStatusError("");
                  }}
                  placeholder="Escreva uma observacao interna sobre o andamento deste pedido."
                  rows={5}
                />
              </label>

              <div className="admin-realgems-orders-form-actions">
                <button
                  type="submit"
                  className="admin-realgems-orders-btn primary"
                  disabled={statusSubmitting}
                >
                  {statusSubmitting ? "Salvando..." : "Salvar status"}
                </button>
              </div>

              {statusError ? <div className="admin-realgems-orders-message error">{statusError}</div> : null}
            </form>
          </div>
        </section>
      </main>
    </div>
  );
};

export default AdminRealgemsOrders;
